import { createApp } from "vue";
import BrandLogo from "@/components/Branding/BrandLogo.vue";
import App from "./App.vue";
import router, { warmRouteDependencies } from "./router";
import store from "./store";

import i18n, { ensureLocaleMessages } from "./plugins/vue-i18n";
import { pageTitle } from "vue-page-title";
import { loadStylesForPath } from "@/utils/styleLoader";
import { scheduleThirdPartyBootstrap } from "@/utils/thirdParty";
import {
  syncSentryRouteContext,
  syncSentrySessionFromStorage,
} from "@/utils/sentryUserContext";
import {
  filterPerformanceTransaction,
  getReplaySessionSampleRate,
  getTracesSampleRate,
  shouldCreateSpanForRequest,
  SENTRY_IGNORE_SPANS,
  SENTRY_IGNORE_RESOURCE_SPANS,
} from "@/utils/sentryTracingFilters";
import {
  isSentryClientEnabled,
  loadSentryBrowser,
  setSentryBrowserReady,
} from "@/utils/sentryClient";
import { isBenignFirebaseAuthPopupError } from "@/utils/firebaseAuthErrors";
// PERFORMANCE: MasonryWall is now imported locally only in components that need it
// (MainCoreValuesAnalyzer/Microlearning.vue and Skills.vue)

// PERFORMANCE: Firebase Firestore is now lazy-loaded only when an error occurs
// This removes ~100KB from the initial bundle for candidate assessment pages

// Firebase app initialization (lightweight, required for auth)
import { app as firebaseApp } from "./firebase";
if (!firebaseApp) {
  throw new Error("Firebase app is not initialized");
}
import helpers from "./helpers/global";
import { safeGetItem } from "./utils/storage";
import { isSupportedLanguageKey, normalizeLanguageKey } from "./helpers/language";

// Custom directives
import { visibilityControl } from "./directives/visibilityControl";
import { setAIEndpointVuexStore } from "@/utils/aiEndpointStoreRef";
import { isInitialPublicAuthPath } from "@/utils/routeFlags";

function scheduleNonCriticalTask(task: () => void): void {
  const idleWindow = window as Window & {
    requestIdleCallback?: (
      callback: () => void,
      options?: { timeout?: number }
    ) => number;
  };

  // `requestIdleCallback` can run immediately on a quiet page. Give the auth
  // form a short, guaranteed head start before monitoring downloads begin.
  window.setTimeout(() => {
    if (idleWindow.requestIdleCallback) {
      idleWindow.requestIdleCallback(task, { timeout: 2000 });
      return;
    }

    task();
  }, 2500);
}

const MAX_BUFFERED_SENTRY_ERRORS = 20;

type SentryErrorContext = {
  source: "vue" | "window" | "unhandledrejection";
  info?: string;
  component?: string;
  filename?: string;
  line?: number;
  column?: number;
};

type BufferedSentryError = {
  error: unknown;
  context: SentryErrorContext;
  occurredAt: number;
};

function normalizeCapturedError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  try {
    return new Error(JSON.stringify(error));
  } catch {
    return new Error(String(error));
  }
}

function getVueComponentName(instance: any): string | undefined {
  return (
    instance?.$options?.name ||
    instance?.$options?.__name ||
    instance?.$?.type?.name ||
    instance?.$?.type?.__name ||
    undefined
  );
}

async function bootstrap() {
  const sentryEnabled =
    Boolean(process.env.VUE_APP_SENTRY_DSN) && isSentryClientEnabled();
  const bufferedSentryErrors: BufferedSentryError[] = [];
  let captureSentryError:
    | ((bufferedError: BufferedSentryError) => void)
    | undefined;

  const captureOrBufferSentryError = (
    error: unknown,
    context: SentryErrorContext
  ): void => {
    if (!sentryEnabled) {
      return;
    }

    const bufferedError = {
      error,
      context,
      occurredAt: Date.now(),
    };

    if (captureSentryError) {
      captureSentryError(bufferedError);
      return;
    }

    if (bufferedSentryErrors.length < MAX_BUFFERED_SENTRY_ERRORS) {
      bufferedSentryErrors.push(bufferedError);
    }
  };

  const captureEarlyWindowError = (event: ErrorEvent): void => {
    captureOrBufferSentryError(
      event.error || new Error(event.message || "Unknown window error"),
      {
        source: "window",
        filename: event.filename || undefined,
        line: event.lineno || undefined,
        column: event.colno || undefined,
      }
    );
  };

  const captureEarlyUnhandledRejection = (
    event: PromiseRejectionEvent
  ): void => {
    if (!isBenignFirebaseAuthPopupError(event.reason)) {
      captureOrBufferSentryError(event.reason, {
        source: "unhandledrejection",
      });
    }
  };

  const stopEarlySentryCapture = (): void => {
    window.removeEventListener("error", captureEarlyWindowError);
    window.removeEventListener(
      "unhandledrejection",
      captureEarlyUnhandledRejection
    );
  };

  if (typeof window !== "undefined") {
    window.addEventListener("unhandledrejection", (event) => {
      if (isBenignFirebaseAuthPopupError(event.reason)) {
        event.preventDefault();
        console.warn(
          "[auth] Suppressed benign Firebase popup race:",
          (event.reason as Error)?.message || event.reason
        );
      }
    });

    if (sentryEnabled) {
      window.addEventListener("error", captureEarlyWindowError);
      window.addEventListener(
        "unhandledrejection",
        captureEarlyUnhandledRejection
      );
    }
  }

  const initialPath = window.location.pathname.toLowerCase();
  const isInitialAuthRoute = isInitialPublicAuthPath(initialPath);
  const routeLocale = initialPath.split("/")[1] || "";
  const storedLang = String(safeGetItem("lang") || "");
  const localeSeed = isSupportedLanguageKey(routeLocale)
    ? routeLocale
    : storedLang;
  const initialLocale = normalizeLanguageKey(localeSeed);

  // The first page's store modules download alongside locale, styles and
  // Firestore below instead of starting once they finish. Its component waits
  // for the router so its CSS lands after the global styles.
  warmRouteDependencies(
    window.location.pathname + window.location.search + window.location.hash,
    { components: false }
  );

  // PERFORMANCE: Firestore persistent cache must be configured before the
  // first getAppFirestore() call anywhere in the app. Loaded as a separate chunk
  // (downloads in parallel with locale/style loading) so firebase/firestore
  // stays out of the entry bundle for candidate assessment pages.
  const firestoreReady = import(
    /* webpackChunkName: "firestore-init" */ "@/utils/firestoreInit"
  )
    .then((m) => m.ensureFirestoreInit())
    .catch(() => undefined);

  // These are independent downloads; awaiting them one by one cost a round
  // trip each before the first paint.
  await Promise.all([
    ensureLocaleMessages(initialLocale),
    loadStylesForPath(window.location.pathname),
    firestoreReady,
  ]);

  const app = createApp(App);
  app.component("BrandLogo", BrandLogo);

  app.use(store);
  setAIEndpointVuexStore(store);

  await store.dispatch("customHost/resolveFromWindowHostname");
  app.use(i18n);
  app.use(router);
  app.use(pageTitle({ router, mixin: true }));

  // PERFORMANCE: Error handler with lazy-loaded Firebase Firestore
  // Firebase is only loaded when an error actually occurs, saving ~100KB on initial load
  const applicationErrorHandler = async (
    err: any,
    instance: any,
    info: string
  ) => {
    captureOrBufferSentryError(err, {
      source: "vue",
      info,
      component: getVueComponentName(instance),
    });

    let userData = {} as any;
    let email = "";
    const userDataEncoded: any = safeGetItem("userData");
    if (userDataEncoded) {
      try {
        userData = JSON.parse(atob(userDataEncoded) || "{}");
        email = userData.email;
      } catch (e: any) {
        // console.error(e);
      }
    }

    const ip = "127.0.0.1";
    const errorDetails = {
      error: {
        message: err.message || "Unknown error",
        stack: err.stack || "Unknown stack",
      },
      info: info,
      email: email,
      ip: ip,
      created_at: helpers.now(),
    };

    // Lazy-load Firebase Firestore only when an error occurs and in production
    if (process.env.NODE_ENV === "production") {
      try {
        const { ensureFirestoreInit, getAppFirestore } = await import(
          "@/utils/appFirestore"
        );
        ensureFirestoreInit();
        const { addDoc, collection } = await import("firebase/firestore");
        const fs = getAppFirestore();
        await addDoc(collection(fs, "errors"), errorDetails);
      } catch (firestoreError) {
        // If Firestore fails to load, just log to console
        console.error("Failed to log error to Firestore:", firestoreError);
      }
    }

    // Always log to console for debugging
    console.error(errorDetails.error.message);
    console.error(errorDetails.error.stack);
  };

  const initializeSentry = async (): Promise<void> => {
    if (!process.env.VUE_APP_SENTRY_DSN || !isSentryClientEnabled()) {
      return;
    }

    try {
      const [Sentry, SentryVue] = await Promise.all([
        loadSentryBrowser(),
        import(/* webpackChunkName: "sentry-vue-tracing" */ "@sentry/vue"),
      ]);
      Sentry.init({
        dsn: process.env.VUE_APP_SENTRY_DSN || "",
        integrations: [
          SentryVue.browserTracingIntegration({
            router,
            shouldCreateSpanForRequest,
            ignoreResourceSpans: [...SENTRY_IGNORE_RESOURCE_SPANS],
            ignorePerformanceApiSpans: [/.*/],
            enableLongTask: false,
            // Click-to-response time (INP), the Core Web Vital for
            // responsiveness.
            enableInp: true,
          }),
          Sentry.replayIntegration(),
        ],
        ignoreTransactions: [
          /\.(js|css|mjs|map|woff2?|png|svg|webp)(\?|$)/i,
          /\/js\//,
          /\/css\//,
          /\/img\//,
        ],
        ignoreSpans: [...SENTRY_IGNORE_SPANS],
        beforeSendTransaction: filterPerformanceTransaction,
        tracesSampler: () => getTracesSampleRate(),
        replaysSessionSampleRate: getReplaySessionSampleRate(),
        replaysOnErrorSampleRate: 1.0,
        enabled: isSentryClientEnabled(),
        environment: process.env.VUE_APP_ENV || "development",
        release: process.env.VUE_APP_SENTRY_RELEASE || undefined,
        debug: process.env.VUE_APP_SENTRY_DEBUG === "on",
        ignoreErrors: [/Pending promise was never set/i],
        beforeSend(event) {
          const message =
            event.exception?.values?.[0]?.value || event.message || "";
          if (isBenignFirebaseAuthPopupError({ message })) {
            return null;
          }
          return event;
        },
      });

      setSentryBrowserReady(Sentry);
      captureSentryError = ({ error, context, occurredAt }) => {
        Sentry.withScope((scope) => {
          scope.setTag("capture_source", context.source);
          scope.setContext("application_error", {
            ...context,
            buffered_for_ms: Math.max(0, Date.now() - occurredAt),
          });
          Sentry.captureException(normalizeCapturedError(error));
        });
      };
      stopEarlySentryCapture();
      bufferedSentryErrors.splice(0).forEach(captureSentryError);
      syncSentrySessionFromStorage();
      syncSentryRouteContext(window.location.pathname);

      // The application handler captures Vue errors explicitly and keeps the
      // existing production Firestore fallback without a late Vue SDK install.
      app.config.errorHandler = applicationErrorHandler;
    } catch (error) {
      stopEarlySentryCapture();
      console.warn("Sentry initialization failed:", error);
    }
  };

  // Monitoring never blocks the first paint. Errors raised before it is ready
  // are buffered above and sent once Sentry starts. On the first signup/login
  // screen it waits until the form is interactive; elsewhere it downloads
  // alongside the page.
  if (!isInitialAuthRoute) {
    void initializeSentry();
  }

  // Register custom directives
  app.directive("visibility-control", visibilityControl);
  app.config.errorHandler = applicationErrorHandler;

  router.afterEach((to) => {
    void loadStylesForPath(String(to.path));
    scheduleThirdPartyBootstrap(String(to.path));
    syncSentryRouteContext(String(to.path));
  });

  app.mount("#app");

  if (isInitialAuthRoute && sentryEnabled) {
    scheduleNonCriticalTask(() => {
      void initializeSentry();
    });
  }

  scheduleThirdPartyBootstrap(window.location.pathname);

  // PRERENDER: Dispatch event when Vue has mounted
  // This tells prerender-spa-plugin when to capture the page
  document.dispatchEvent(new Event("render-event"));
}

void bootstrap().catch((err) => {
  console.error("Application bootstrap failed:", err);
});
